import React from "react";
import { ArrowLongLeftIcon, ArrowLongRightIcon } from "@heroicons/react/20/solid";

// Main Table Container
export const Table = ({ children, className = "" }) => {
  return (
    <div className="flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table
              className={`min-w-full divide-y divide-gray-300 ${className}`}
            >
              {children}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Table Header
export const THead = ({ children, className = "" }) => {
  return <thead className={`bg-gray-50 ${className}`}>{children}</thead>;
};

// Table Body
export const TBody = ({ children, className = "" }) => {
  return (
    <tbody className={`divide-y divide-gray-200 bg-white ${className}`}>
      {children}
    </tbody>
  );
};

// Table Row
export const TR = ({
  children,
  className = "",
  hover = true,
  ...props
}) => {
  const hoverClass = hover ? "hover:bg-gray-50" : "";
  return (
    <tr className={`${hoverClass} ${className}`} {...props}>
      {children}
    </tr>
  );
};

export const SortLabel = ({
  children,
  direction = null,
  sortIndex = null,
  onClick,
  className = "",
}) => {
  const isActive = direction === "asc" || direction === "desc";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-left text-black ${
        isActive ? "font-bold" : "font-semibold"
      } ${className}`}
    >
      <span>{children}</span>
      {direction === "asc" && (
        <ArrowLongRightIcon className="h-4 w-4 text-black" aria-hidden="true" />
      )}
      {direction === "desc" && (
        <ArrowLongLeftIcon className="h-4 w-4 text-black" aria-hidden="true" />
      )}
      {sortIndex != null && (
        <span className="text-xs font-bold text-black">{sortIndex}</span>
      )}
    </button>
  );
};

// Table Head Cell
export const TH = ({
  children,
  className = "",
  scope = "col",
  first = false,
  last = false,
  compact = false,
  ...props
}) => {
  const baseClasses = compact
    ? "py-1.5 text-left text-sm font-semibold text-gray-900"
    : "py-2 text-left text-sm font-semibold text-gray-900";
  const firstClasses = first ? (compact ? "pl-2 pr-1 sm:pl-2" : "pl-4 pr-2 sm:pl-6") : (compact ? "px-1.5" : "px-2");
  const lastClasses = last ? (compact ? "relative pl-1.5 pr-2 sm:pr-2" : "relative pl-2 pr-4 sm:pr-6") : "";

  return (
    <th
      scope={scope}
      className={`${baseClasses} ${firstClasses} ${lastClasses} ${className}`}
      {...props}
    >
      {children}
    </th>
  );
};

// Table Data Cell
export const TD = ({
  children,
  className = "",
  first = false,
  last = false,
  compact = false,
  ...props
}) => {
  const baseClasses = compact
    ? "whitespace-nowrap py-1 text-sm"
    : "whitespace-nowrap py-2 text-sm";
  const firstClasses = first
    ? (compact ? "pl-2 pr-1 font-medium text-gray-900 sm:pl-2" : "pl-4 pr-2 font-medium text-gray-900 sm:pl-6")
    : (compact ? "px-1.5 text-gray-500" : "px-2 text-gray-500");
  const lastClasses = last
    ? (compact ? "relative pl-1.5 pr-2 text-right font-medium sm:pr-2" : "relative pl-2 pr-4 text-right font-medium sm:pr-6")
    : "";

  return (
    <td
      className={`${baseClasses} ${firstClasses} ${lastClasses} ${className}`}
      {...props}
    >
      {children}
    </td>
  );
};

// Loading Row Component
export const LoadingRow = ({ colSpan, message = "Loading..." }) => {
  return (
    <TR hover={false}>
      <TD
        colSpan={colSpan}
        className="px-6 py-4 text-center text-sm text-gray-500"
        first={false}
      >
        {message}
      </TD>
    </TR>
  );
};

// Empty Row Component
export const EmptyRow = ({ colSpan, message = "No data found." }) => {
  return (
    <TR hover={false}>
      <TD
        colSpan={colSpan}
        className="px-6 py-4 text-center text-sm text-gray-500"
        first={false}
      >
        {message}
      </TD>
    </TR>
  );
};

// Action Button Component
export const ActionButton = ({
  children,
  onClick,
  variant = "primary",
  title,
  className = "",
  type = "button",
  ...props
}) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-500 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    success: "bg-green-600 hover:bg-green-500 text-white",
    warning: "bg-yellow-600 hover:bg-yellow-500 text-white",
    secondary: "bg-gray-600 hover:bg-gray-500 text-white",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      className={`mr-2 px-2 py-1.5 rounded-md ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Badge Component for status indicators
export const Badge = ({
  children,
  variant = "default",
  className = "",
  ...props
}) => {
  const variants = {
    default: "bg-gray-50 text-gray-700 ring-gray-600/20",
    success: "bg-green-50 text-green-700 ring-green-600/20",
    danger: "bg-red-50 text-red-700 ring-red-600/20",
    warning: "bg-yellow-50 text-yellow-700 ring-yellow-600/20",
    info: "bg-blue-50 text-blue-700 ring-blue-600/20",
    primary: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  };

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </span>
  );
};

// Export all components as default
const TableComponents = {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  SortLabel,
  LoadingRow,
  EmptyRow,
  ActionButton,
  Badge,
};

export default TableComponents;
